// Aggregate stats / overview screen + CSV export.

import { useCallback, useMemo, useState } from "react";
import api from "../../api";
import Icon from "./icons";
import Shell from "./Shell";
import {
  ErrorState,
  LoadingState,
  RefreshButton,
  Sparkline,
  apiErrorMessage,
  formatDuration,
  formatMoney
} from "./shared";
import useBssPoll from "../../hooks/useBssPoll";

const PERIODS = ["7d", "14d", "30d", "90d"];
const PHASE_TINT = {
  Planner: "var(--bss-phase-planning)",
  Implementer: "var(--bss-phase-implementing)",
  Tester: "var(--bss-phase-testing)",
  Reviewer: "var(--bss-phase-reviewing)",
  Fixer: "var(--bss-phase-fixing)",
  Pusher: "var(--bss-phase-pushing)"
};
// Per-mode tint for the run-count donut/legend. Keyed by canonical mode
// (matches the backend byMode + the RunsPage badge colors). `implement` has no
// RunsPage badge, so it borrows the Implementer phase color.
const MODE_TINT = {
  implement: "var(--bss-phase-implementing)",
  review: "var(--bss-phase-reviewing)",
  "impact-analysis": "var(--bss-phase-testing)",
  investigate: "var(--bss-boom-pink)",
  "address-comments": "var(--bss-info)",
  "address-checks": "var(--bss-danger)",
  refresh: "var(--bss-phase-pushing)",
  test_plan: "var(--bss-phase-planning)",
  adoption: "var(--bss-fg-muted)"
};

// Per-project tint for the "Reviews by project" donut. Known projects get a
// stable color; anything else cycles the palette by index so new projects still
// render distinctly. NOTE: several --bss tokens alias the same hex (info ==
// phase-implementing #5b8dff; boom-pink == phase-pushing #ff3d80), so every
// entry below resolves to a UNIQUE color — two projects must never share a swatch.
// "Other" (every project outside JIRA_ALLOWED_PROJECTS) owns the muted gray, so
// it's kept OUT of the cycling palette below.
const PROJECT_TINT = {
  CRM: "var(--bss-boom-pink)", // #ff3d80
  AI: "var(--bss-info)", // #5b8dff
  BF: "var(--bss-phase-planning)", // #c5a3ff
  BSCPF: "var(--bss-phase-reviewing)", // #ffb37a
  BR: "var(--bss-phase-testing)", // #8de4b6
  Other: "var(--bss-fg-muted)" // #9a9aa5
};
const PROJECT_PALETTE = [
  "var(--bss-boom-pink)", // #ff3d80
  "var(--bss-info)", // #5b8dff
  "var(--bss-success)", // #3dd07b
  "var(--bss-phase-reviewing)", // #ffb37a
  "var(--bss-phase-planning)", // #c5a3ff
  "var(--bss-phase-testing)", // #8de4b6
  "var(--bss-phase-fixing)", // #f4a93a
  "var(--bss-danger)" // #ff5247
];
// Assign a UNIQUE color to every project shown in the donut + legend. Tinted
// projects keep their reserved tint; the rest take the next palette color not
// already used in THIS dataset — so two slices can never share a swatch,
// regardless of ordering or how many projects appear. (Index-cycling the palette
// could collide with a tint: an allow-listed-but-untinted project like INF could
// land on the boom-pink slot already owned by CRM.)
export const assignProjectColors = (rows) => {
  const used = new Set();
  for (const p of rows || []) {
    const tint = PROJECT_TINT[p.project];
    if (tint) used.add(tint);
  }
  const map = {};
  let palIdx = 0;
  for (const p of rows || []) {
    const tint = PROJECT_TINT[p.project];
    if (tint) {
      map[p.project] = tint;
      continue;
    }
    while (palIdx < PROJECT_PALETTE.length && used.has(PROJECT_PALETTE[palIdx])) palIdx += 1;
    const color = PROJECT_PALETTE[palIdx % PROJECT_PALETTE.length];
    map[p.project] = color;
    used.add(color);
    palIdx += 1;
  }
  return map;
};

const Tile = ({ label, value, delta, deltaGood, spark, sparkColor }) => (
  <div className="bss-card bss-stat">
    <div className="bss-stat-label">{label}</div>
    <div className="bss-stat-value">{value}</div>
    {delta != null && (
      <div className="bss-stat-meta">
        <span className={deltaGood ? "bss-up" : "bss-down"}>
          {deltaGood ? "↑" : "↓"} {delta}
        </span>
      </div>
    )}
    {spark && spark.length > 1 && <Sparkline data={spark} color={sparkColor} />}
  </div>
);

const PhaseDonut = ({ data, total }) => {
  if (!data || data.length === 0) return null;
  const sum = data.reduce((s, d) => s + (d.pct || 0), 0) || 1;
  const r = 56;
  const c = 2 * Math.PI * r;
  let offset = 0;
  return (
    <div
      style={{
        position: "relative",
        width: 140,
        height: 140,
        flexShrink: 0
      }}
    >
      <svg
        width="140"
        height="140"
        viewBox="0 0 140 140"
        style={{ transform: "rotate(-90deg)" }}
      >
        <circle
          cx="70"
          cy="70"
          r={r}
          fill="none"
          stroke="var(--bss-surface-2)"
          strokeWidth="18"
        />
        {data.map((d, i) => {
          const dash = ((d.pct || 0) / sum) * c;
          const seg = (
            <circle
              key={d.name || i}
              cx="70"
              cy="70"
              r={r}
              fill="none"
              stroke={PHASE_TINT[d.name] || "var(--bss-boom-pink)"}
              strokeWidth="18"
              strokeDasharray={`${dash} ${c - dash}`}
              strokeDashoffset={-offset}
            />
          );
          offset += dash;
          return seg;
        })}
      </svg>
      <div
        style={{
          position: "absolute",
          inset: 30,
          borderRadius: 999,
          background: "var(--bss-surface)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        <div
          style={{
            fontSize: 18,
            fontWeight: 600,
            color: "var(--bss-fg-strong)",
            fontFamily: "var(--bss-font-mono)"
          }}
        >
          {formatMoney(total)}
        </div>
        <div style={{ fontSize: 10.5, color: "var(--bss-fg-muted)" }}>
          TOTAL
        </div>
      </div>
    </div>
  );
};

// Run-count donut — same geometry as PhaseDonut, but segments are sized by
// run COUNT (via pct) and tinted per mode, with the total run count in the hub.
const CountDonut = ({ data, total }) => {
  if (!data || data.length === 0) return null;
  const sum = data.reduce((s, d) => s + (d.pct || 0), 0) || 1;
  const r = 56;
  const c = 2 * Math.PI * r;
  let offset = 0;
  return (
    <div
      style={{ position: "relative", width: 140, height: 140, flexShrink: 0 }}
    >
      <svg
        width="140"
        height="140"
        viewBox="0 0 140 140"
        style={{ transform: "rotate(-90deg)" }}
      >
        <circle
          cx="70"
          cy="70"
          r={r}
          fill="none"
          stroke="var(--bss-surface-2)"
          strokeWidth="18"
        />
        {data.map((d, i) => {
          const dash = ((d.pct || 0) / sum) * c;
          const seg = (
            <circle
              key={d.mode || i}
              cx="70"
              cy="70"
              r={r}
              fill="none"
              stroke={MODE_TINT[d.mode] || "var(--bss-boom-pink)"}
              strokeWidth="18"
              strokeDasharray={`${dash} ${c - dash}`}
              strokeDashoffset={-offset}
            />
          );
          offset += dash;
          return seg;
        })}
      </svg>
      <div
        style={{
          position: "absolute",
          inset: 30,
          borderRadius: 999,
          background: "var(--bss-surface)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        <div
          style={{
            fontSize: 22,
            fontWeight: 600,
            color: "var(--bss-fg-strong)",
            fontFamily: "var(--bss-font-mono)"
          }}
        >
          {total}
        </div>
        <div style={{ fontSize: 10.5, color: "var(--bss-fg-muted)" }}>RUNS</div>
      </div>
    </div>
  );
};

// Review-runs-by-project donut — same geometry as CountDonut, tinted per project
// with the review total in the hub.
const ProjectDonut = ({ data, total, colors }) => {
  if (!data || data.length === 0) return null;
  const sum = data.reduce((s, d) => s + (d.pct || 0), 0) || 1;
  const r = 56;
  const c = 2 * Math.PI * r;
  let offset = 0;
  return (
    <div
      style={{ position: "relative", width: 140, height: 140, flexShrink: 0 }}
    >
      <svg
        width="140"
        height="140"
        viewBox="0 0 140 140"
        style={{ transform: "rotate(-90deg)" }}
      >
        <circle
          cx="70"
          cy="70"
          r={r}
          fill="none"
          stroke="var(--bss-surface-2)"
          strokeWidth="18"
        />
        {data.map((d, i) => {
          const dash = ((d.pct || 0) / sum) * c;
          const seg = (
            <circle
              key={d.project || i}
              cx="70"
              cy="70"
              r={r}
              fill="none"
              stroke={colors[d.project]}
              strokeWidth="18"
              strokeDasharray={`${dash} ${c - dash}`}
              strokeDashoffset={-offset}
            />
          );
          offset += dash;
          return seg;
        })}
      </svg>
      <div
        style={{
          position: "absolute",
          inset: 30,
          borderRadius: 999,
          background: "var(--bss-surface)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        <div
          style={{
            fontSize: 22,
            fontWeight: 600,
            color: "var(--bss-fg-strong)",
            fontFamily: "var(--bss-font-mono)"
          }}
        >
          {total}
        </div>
        <div style={{ fontSize: 10.5, color: "var(--bss-fg-muted)" }}>
          REVIEWS
        </div>
      </div>
    </div>
  );
};

// "Reviews by project" card — donut + legend, grouped by Jira project key.
const ReviewsByProjectCard = ({ data }) => {
  const rows = data || [];
  const total = rows.reduce((s, p) => s + (p.count || 0), 0);
  const colors = assignProjectColors(rows);
  return (
    <div className="bss-chart-card">
      <h3>Reviews by project</h3>
      <div className="bss-sub">Review runs grouped by Jira project</div>
      {rows.length === 0 ? (
        <div
          style={{
            color: "var(--bss-fg-muted)",
            fontSize: 12.5,
            marginTop: 16
          }}
        >
          No reviews in this window.
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 24,
            marginTop: 18
          }}
        >
          <ProjectDonut data={rows} total={total} colors={colors} />
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              gap: 6
            }}
          >
            {rows.map((p) => (
              <div
                key={p.project}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  fontSize: 12.5
                }}
              >
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 2,
                    background: colors[p.project],
                    flexShrink: 0
                  }}
                />
                <span style={{ color: "var(--bss-fg)" }}>{p.project}</span>
                <span
                  style={{ marginLeft: "auto", color: "var(--bss-fg-muted)" }}
                >
                  {Math.round(p.pct)}%
                </span>
                <span
                  style={{
                    width: 56,
                    fontFamily: "var(--bss-font-mono)",
                    color: "var(--bss-fg)",
                    textAlign: "right"
                  }}
                >
                  {p.count}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const VERDICT_TINT = {
  approve: "var(--bss-success)",
  advisory: "var(--bss-info)",
  changes: "var(--bss-danger)",
  failed: "var(--bss-fg-muted)"
};

// Score bar tint: 8-10 green (would-merge), 4-7 amber, 1-3 red (blocker).
const scoreColor = (score) => {
  if (score >= 8) return "var(--bss-success)";
  if (score >= 4) return "var(--bss-info)";
  return "var(--bss-danger)";
};

// Review-precision sections (funnel / verdict mix / score dist / per-lens).
// Tune lenses with data: a lens with a high trim% over-flags (lots proposed,
// little kept); a high 👎-rate means humans reject what survives.
function ReviewPrecision({ stats }) {
  if (!stats) return null;
  const f = stats.funnel || { candidates: 0, verified: 0, posted: 0, dropPct: 0 };
  const scoreMax = Math.max(1, ...(stats.scoreDist || []).map((s) => s.count || 0));
  return (
    <div style={{ marginTop: 24 }}>
      <div className="bss-page-hd" style={{ marginBottom: 12 }}>
        <div>
          <h1 style={{ fontSize: 18 }}>Review precision</h1>
          <div className="bss-page-hd-sub">
            {stats.reviews} reviews · funnel, verdicts, scores &amp; per-lens flagging
          </div>
        </div>
      </div>

      <div className="bss-stats-grid">
        <Tile label="Reviews" value={stats.reviews} />
        <Tile label="Avg score" value={stats.avgScore || "—"} />
        <Tile label="👎 reject rate" value={`${stats.rejectRate || 0}%`} />
        <Tile label="Funnel drop" value={`${f.dropPct || 0}%`} />
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
          gap: 16,
          marginBottom: 16
        }}
      >
        <div className="bss-chart-card">
          <h3>Funnel</h3>
          <div className="bss-sub">candidates → verified → posted (verifier trims the rest)</div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              marginTop: 24,
              fontFamily: "var(--bss-font-mono)"
            }}
          >
            {[
              ["Candidates", f.candidates, "var(--bss-fg-muted)"],
              ["Verified", f.verified, "var(--bss-info)"],
              ["Posted", f.posted, "var(--bss-boom-pink)"]
            ].map(([label, val, color], i, arr) => (
              <div key={label} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 26, fontWeight: 600, color }}>{val}</div>
                  <div
                    style={{ fontSize: 10.5, color: "var(--bss-fg-muted)", fontFamily: "var(--bss-font-sans)" }}
                  >
                    {label}
                  </div>
                </div>
                {i < arr.length - 1 && (
                  <span style={{ color: "var(--bss-fg-subtle)", fontSize: 18 }}>→</span>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="bss-chart-card">
          <h3>Verdict mix</h3>
          <div className="bss-sub">per review</div>
          <div
            style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 18 }}
          >
            {(stats.verdictMix || []).map((v) => (
              <div
                key={v.verdict}
                style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12.5 }}
              >
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 2,
                    background: VERDICT_TINT[v.verdict] || "var(--bss-boom-pink)",
                    flexShrink: 0
                  }}
                />
                <span style={{ color: "var(--bss-fg)", width: 130 }}>{v.label}</span>
                <div className="bss-ph-bar" style={{ flex: 1, height: 6 }}>
                  <div
                    style={{
                      width: `${v.pct}%`,
                      background: VERDICT_TINT[v.verdict] || "var(--bss-boom-pink)"
                    }}
                  />
                </div>
                <span style={{ width: 60, textAlign: "right", color: "var(--bss-fg-muted)" }}>
                  {v.count} · {v.pct}%
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bss-chart-card" style={{ marginBottom: 16 }}>
        <h3>Score distribution</h3>
        <div className="bss-sub">merge-safety score (1–10) across reviews</div>
        <div className="bss-bar-chart" style={{ marginTop: 12 }}>
          {(stats.scoreDist || []).map((s) => (
            <div
              key={s.score}
              className="bss-bar"
              title={`score ${s.score}: ${s.count}`}
              style={{ height: "100%" }}
            >
              <div
                className="bss-bar-seg ok"
                style={{
                  height: `${((s.count || 0) / scoreMax) * 100}%`,
                  background: scoreColor(s.score)
                }}
              />
            </div>
          ))}
        </div>
        <div className="bss-bar-axis">
          {(stats.scoreDist || []).map((s) => (
            <span key={s.score}>{s.score}</span>
          ))}
        </div>
      </div>

      <div className="bss-chart-card">
        <h3>Per-lens flagging</h3>
        <div className="bss-sub">
          high trim% = over-flagging (proposed, then dropped by the verifier); high 👎% = humans reject what ships
        </div>
        <div style={{ marginTop: 14, display: "flex", flexDirection: "column" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(0,1fr) 90px 70px 70px 70px 80px",
              gap: 12,
              fontSize: 11,
              color: "var(--bss-fg-subtle)",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              paddingBottom: 8,
              borderBottom: "1px solid var(--bss-border)"
            }}
          >
            <span>Lens</span>
            <span style={{ textAlign: "right" }}>Proposed</span>
            <span style={{ textAlign: "right" }}>Kept</span>
            <span style={{ textAlign: "right" }}>Trim</span>
            <span style={{ textAlign: "right" }}>👎</span>
            <span style={{ textAlign: "right" }}>Cost</span>
          </div>
          {(stats.byLens || []).map((l) => (
            <div
              key={l.lens}
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(0,1fr) 90px 70px 70px 70px 80px",
                gap: 12,
                padding: "9px 0",
                borderBottom: "1px solid var(--bss-border)",
                alignItems: "center",
                fontSize: 12.5
              }}
            >
              <span style={{ color: "var(--bss-fg-strong)", fontWeight: 500 }}>
                {l.label}
                {l.silentRuns > 0 && (
                  <span style={{ color: "var(--bss-fg-subtle)", fontWeight: 400 }}>
                    {" "}· {l.silentRuns} silent
                  </span>
                )}
              </span>
              <span style={{ textAlign: "right", fontFamily: "var(--bss-font-mono)" }}>{l.proposed}</span>
              <span style={{ textAlign: "right", fontFamily: "var(--bss-font-mono)" }}>{l.kept}</span>
              <span
                style={{
                  textAlign: "right",
                  fontFamily: "var(--bss-font-mono)",
                  color: l.trimPct >= 70 ? "var(--bss-danger)" : "var(--bss-fg-muted)"
                }}
              >
                {l.trimPct}%
              </span>
              <span
                style={{
                  textAlign: "right",
                  fontFamily: "var(--bss-font-mono)",
                  color: l.rejectRate >= 30 ? "var(--bss-danger)" : "var(--bss-fg-muted)"
                }}
              >
                {l.rejectRate}%
              </span>
              <span style={{ textAlign: "right", fontFamily: "var(--bss-font-mono)", color: "var(--bss-fg-muted)" }}>
                {formatMoney(l.costUsd)}
              </span>
            </div>
          ))}
          {(stats.byLens || []).length === 0 && (
            <div style={{ color: "var(--bss-fg-muted)", fontSize: 12.5, paddingTop: 10 }}>
              No reviews in this window.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function StatsPage() {
  const [period, setPeriod] = useState("14d");
  const [exporting, setExporting] = useState(false);

  const fetchStats = useCallback(
    () => api.bssStats({ period }).then((res) => res.data),
    [period]
  );
  const { data: stats, error, loading, refresh } = useBssPoll(fetchStats, {
    deps: [period],
  });

  // Review-precision sections poll independently — they must not block (or be
  // blocked by) the main run stats above. Refetch on period change.
  const fetchReviewStats = useCallback(
    () => api.bssReviewStats({ period }).then((res) => res.data),
    [period]
  );
  const { data: reviewStats } = useBssPoll(fetchReviewStats, { deps: [period] });

  const maxBar = useMemo(() => {
    const weekly = (stats && stats.weekly) || [];
    return Math.max(1, ...weekly.map((d) => (d.ok || 0) + (d.fail || 0)));
  }, [stats]);

  const exportCsv = useCallback(() => {
    setExporting(true);
    api
      .bssStatsExport({ period })
      .then((res) => {
        const blob = new Blob([res.data], {
          type: res.headers["content-type"] || "text/csv"
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `bss-runs-${period}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => {
        // CSV export is best-effort; a failed download is non-blocking.
      })
      .finally(() => setExporting(false));
  }, [period]);

  let body;
  if (loading) body = <LoadingState label="Loading stats…" />;
  else if (error || !stats)
    body = <ErrorState message={apiErrorMessage(error)} onRetry={refresh} />;
  else {
    const topMax = Math.max(
      1,
      ...(stats.topSpenders || []).map((s) => s.costUsd || 0)
    );
    body = (
      <>
        <div className="bss-stats-grid">
          <Tile
            label="Runs"
            value={stats.runs}
            delta={`${Math.abs(stats.runsDelta)} vs previous`}
            deltaGood={stats.runsDelta >= 0}
            spark={(stats.weekly || []).map((d) => (d.ok || 0) + (d.fail || 0))}
            sparkColor="var(--bss-boom-pink)"
          />
          <Tile
            label="Success rate"
            value={`${Math.round(stats.successRate)}%`}
            delta={`${Math.abs(stats.successRateDelta)}pp vs previous`}
            deltaGood={stats.successRateDelta >= 0}
          />
          <Tile
            label="Total spend"
            value={formatMoney(stats.totalSpendUsd)}
            delta={`${formatMoney(
              Math.abs(stats.totalSpendDelta)
            )} vs previous`}
            deltaGood={stats.totalSpendDelta <= 0}
          />
          <Tile
            label="Avg cost / run"
            value={formatMoney(stats.avgCostPerRunUsd)}
            delta={`${formatMoney(
              Math.abs(stats.avgCostPerRunDelta)
            )} vs previous`}
            deltaGood={stats.avgCostPerRunDelta <= 0}
          />
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1.6fr) minmax(0, 1fr)",
            gap: 16,
            marginBottom: 16
          }}
        >
          <div className="bss-chart-card">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between"
              }}
            >
              <div>
                <h3>Runs per day</h3>
                <div className="bss-sub">Stacked by outcome</div>
              </div>
              <div className="bss-legend">
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6
                  }}
                >
                  <span
                    className="bss-legend-dot"
                    style={{
                      background:
                        "color-mix(in oklab, var(--bss-success) 80%, transparent)"
                    }}
                  />{" "}
                  Success
                </span>
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6
                  }}
                >
                  <span
                    className="bss-legend-dot"
                    style={{
                      background:
                        "color-mix(in oklab, var(--bss-danger) 80%, transparent)"
                    }}
                  />{" "}
                  Failed
                </span>
              </div>
            </div>
            <div className="bss-bar-chart">
              {(stats.weekly || []).map((d, i) => (
                <div
                  key={d.date || i}
                  className="bss-bar"
                  title={`${d.ok || 0} success · ${d.fail || 0} failed`}
                  style={{ height: "100%" }}
                >
                  <div
                    className="bss-bar-seg fail"
                    style={{
                      height: `${((d.fail || 0) / maxBar) * 100}%`
                    }}
                  />
                  <div
                    className="bss-bar-seg ok"
                    style={{
                      height: `${((d.ok || 0) / maxBar) * 100}%`
                    }}
                  />
                </div>
              ))}
            </div>
            <div className="bss-bar-axis">
              {(stats.weekly || []).map((d, i) => (
                <span key={d.date || i}>{d.date ? d.date.slice(5) : ""}</span>
              ))}
            </div>
          </div>

          <div className="bss-chart-card">
            <h3>Run duration</h3>
            <div className="bss-sub">P50 and P95 for the window</div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 24,
                marginTop: 28
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: 12,
                    color: "var(--bss-fg-muted)",
                    marginBottom: 4
                  }}
                >
                  P50
                </div>
                <div
                  style={{
                    fontSize: 28,
                    fontWeight: 600,
                    color: "var(--bss-fg-strong)",
                    fontFamily: "var(--bss-font-mono)"
                  }}
                >
                  {formatDuration(stats.p50DurationSec)}
                </div>
              </div>
              <div
                style={{
                  width: 1,
                  height: 56,
                  background: "var(--bss-border)"
                }}
              />
              <div>
                <div
                  style={{
                    fontSize: 12,
                    color: "var(--bss-fg-muted)",
                    marginBottom: 4
                  }}
                >
                  P95
                </div>
                <div
                  style={{
                    fontSize: 28,
                    fontWeight: 600,
                    color: "var(--bss-fg-strong)",
                    fontFamily: "var(--bss-font-mono)"
                  }}
                >
                  {formatDuration(stats.p95DurationSec)}
                </div>
              </div>
            </div>
            <div
              style={{
                marginTop: 28,
                display: "flex",
                gap: 24,
                fontSize: 12.5,
                color: "var(--bss-fg-muted)"
              }}
            >
              <span>Active runs: {stats.activeRuns}</span>
              <span>Queued: {stats.queuedRuns}</span>
            </div>
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
            gap: 16,
            marginBottom: 16
          }}
        >
          <div className="bss-chart-card">
            <h3>Where the money goes</h3>
            <div className="bss-sub">Spend breakdown by phase</div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 24,
                marginTop: 18
              }}
            >
              <PhaseDonut
                data={stats.byPhase || []}
                total={stats.totalSpendUsd}
              />
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: 6
                }}
              >
                {(stats.byPhase || []).map((p) => (
                  <div
                    key={p.name}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      fontSize: 12.5
                    }}
                  >
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: 2,
                        background:
                          PHASE_TINT[p.name] || "var(--bss-boom-pink)",
                        flexShrink: 0
                      }}
                    />
                    <span style={{ color: "var(--bss-fg)" }}>{p.name}</span>
                    <span
                      style={{
                        marginLeft: "auto",
                        color: "var(--bss-fg-muted)"
                      }}
                    >
                      {Math.round(p.pct)}%
                    </span>
                    <span
                      style={{
                        width: 56,
                        fontFamily: "var(--bss-font-mono)",
                        color: "var(--bss-fg)",
                        textAlign: "right"
                      }}
                    >
                      {formatMoney(p.amountUsd)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="bss-chart-card">
            <h3>Where the runs go</h3>
            <div className="bss-sub">Run count by type</div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 24,
                marginTop: 18
              }}
            >
              <CountDonut data={stats.byMode || []} total={stats.runs} />
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: 6
                }}
              >
                {(stats.byMode || []).map((m) => (
                  <div
                    key={m.mode}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      fontSize: 12.5
                    }}
                  >
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: 2,
                        background: MODE_TINT[m.mode] || "var(--bss-boom-pink)",
                        flexShrink: 0
                      }}
                    />
                    <span style={{ color: "var(--bss-fg)" }}>{m.label}</span>
                    <span
                      style={{
                        marginLeft: "auto",
                        color: "var(--bss-fg-muted)"
                      }}
                    >
                      {Math.round(m.pct)}%
                    </span>
                    <span
                      style={{
                        width: 56,
                        fontFamily: "var(--bss-font-mono)",
                        color: "var(--bss-fg)",
                        textAlign: "right"
                      }}
                    >
                      {m.count}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="bss-chart-card">
            <h3>Most expensive runs</h3>
            <div className="bss-sub">Top spenders for the window</div>
            <div style={{ marginTop: 16 }}>
              {(stats.topSpenders || []).map((s) => (
                <div key={s.key} className="bss-spend-row">
                  <span
                    style={{
                      fontFamily: "var(--bss-font-mono)",
                      fontSize: 12,
                      color: "var(--bss-fg)"
                    }}
                  >
                    {s.key}
                  </span>
                  <div className="bss-ph-bar">
                    <div
                      style={{
                        width: `${((s.costUsd || 0) / topMax) * 100}%`,
                        background: s.over
                          ? "var(--bss-danger)"
                          : "var(--bss-boom-pink)"
                      }}
                    />
                  </div>
                  <span
                    className="bss-amt"
                    style={{
                      color: s.over ? "var(--bss-danger)" : "var(--bss-fg)"
                    }}
                  >
                    {formatMoney(s.costUsd)}
                  </span>
                </div>
              ))}
              {(stats.topSpenders || []).length === 0 && (
                <div
                  style={{
                    color: "var(--bss-fg-muted)",
                    fontSize: 12.5
                  }}
                >
                  No spend recorded for this window.
                </div>
              )}
            </div>
          </div>

          <ReviewsByProjectCard data={stats.reviewsByProject} />
        </div>

        <div className="bss-chart-card">
          <h3>Failure clusters</h3>
          <div className="bss-sub">Grouped by error signature</div>
          <div
            style={{
              marginTop: 14,
              display: "flex",
              flexDirection: "column"
            }}
          >
            {(stats.failureClusters || []).map((f, i, arr) => (
              <div
                key={f.signature}
                style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1fr) 120px 100px 110px",
                  gap: 14,
                  padding: "10px 0",
                  borderBottom:
                    i < arr.length - 1 ? "1px solid var(--bss-border)" : "none",
                  alignItems: "center"
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8
                  }}
                >
                  <Icon.AlertTriangle
                    size={13}
                    style={{ color: "var(--bss-danger)" }}
                  />
                  <span
                    style={{
                      fontFamily: "var(--bss-font-mono)",
                      fontSize: 12.5,
                      color: "var(--bss-fg-strong)",
                      fontWeight: 500
                    }}
                  >
                    {f.signature}
                  </span>
                </div>
                <div className="bss-ph-bar" style={{ height: 5 }}>
                  <div
                    style={{
                      width: `${f.pct || 0}%`,
                      background: "var(--bss-danger)"
                    }}
                  />
                </div>
                <span style={{ fontSize: 12.5, color: "var(--bss-fg)" }}>
                  {f.count} runs
                </span>
                <span
                  style={{
                    fontSize: 12,
                    color: "var(--bss-fg-muted)"
                  }}
                >
                  last {f.lastOccurredAt}
                </span>
              </div>
            ))}
            {(stats.failureClusters || []).length === 0 && (
              <div
                style={{
                  color: "var(--bss-fg-muted)",
                  fontSize: 12.5
                }}
              >
                No failures in this window.
              </div>
            )}
          </div>
        </div>
      </>
    );
  }

  return (
    <Shell>
      <div className="bss-page-hd">
        <div>
          <h1>Overview</h1>
          <div className="bss-page-hd-sub">
            Aggregate stats across all agent runs.
          </div>
        </div>
        <div className="bss-page-hd-right">
          <RefreshButton onClick={refresh} loading={loading} />
          <div className="bss-filter-tabs">
            {PERIODS.map((p) => (
              <button
                key={p}
                type="button"
                className={`bss-filter-tab ${
                  period === p ? "is-active" : ""
                }`.trim()}
                onClick={() => setPeriod(p)}
              >
                {p}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="bss-btn bss-btn--ghost bss-btn--sm"
            onClick={exportCsv}
            disabled={exporting}
          >
            <Icon.Folder size={13} /> {exporting ? "Exporting…" : "Export CSV"}
          </button>
        </div>
      </div>
      {body}
      {reviewStats && <ReviewPrecision stats={reviewStats} />}
    </Shell>
  );
}
